import React, { Component } from 'react';
import SecuredZone from '../SecuredZone/SecuredZone';
import LinearProgress from '@material-ui/core/LinearProgress';
import moment from 'moment';
import 'moment/locale/de';
import API from '../../../api/api';
import { Link as RouterLink } from 'react-router-dom';
import PostAddIcon from '@material-ui/icons/PostAdd';
import BuildIcon from '@material-ui/icons/Build';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import PeopleIcon from '@material-ui/icons/People';
import EventNoteIcon from '@material-ui/icons/EventNote';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import WebIcon from '@material-ui/icons/Web';
import './Dashboard.css';

moment.locale('de');

const garageApi = new API();

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function byNewest(items) {
  return [...items].sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
}

function greeting() {
  const hour = Number(moment().format('H'));
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

class AdminDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      stats: {
        posts: 0,
        services: 0,
        gallery: 0,
        employees: 0,
        appointments: 0,
        contacts: 0,
      },
      recentPosts: [],
      inbox: [],
    };
  }

  componentDidMount() {
    this.loadDashboardData();
  }

  loadDashboardData() {
    Promise.all([
      garageApi.endpoints.posts.getAll().catch(() => ({ data: [] })),
      garageApi.endpoints.services.getAll().catch(() => ({ data: [] })),
      garageApi.endpoints.gallery.getAll().catch(() => ({ data: [] })),
      garageApi.endpoints.employees.getAll().catch(() => ({ data: [] })),
      garageApi.endpoints.formAppointments.getPage({ get: { limit: 8, offset: 0 } }).catch(() => ({ data: [] })),
      garageApi.endpoints.formContacts.getPage({ get: { limit: 8, offset: 0 } }).catch(() => ({ data: [] })),
    ]).then(([postsRes, servicesRes, galleryRes, employeesRes, appointmentsRes, contactsRes]) => {
      const posts = asList(postsRes.data);
      const services = asList(servicesRes.data);
      const gallery = asList(galleryRes.data);
      const employees = asList(employeesRes.data);
      const appointments = asList(appointmentsRes.data);
      const contacts = asList(contactsRes.data);

      const inbox = byNewest([
        ...appointments.map((item) => ({ kind: 'termin', ...item })),
        ...contacts.map((item) => ({ kind: 'kontakt', ...item })),
      ]).slice(0, 7);

      this.setState({
        stats: {
          posts: posts.length,
          services: services.length,
          gallery: gallery.length,
          employees: employees.length,
          appointments: appointmentsRes.data?.count ?? appointments.length,
          contacts: contactsRes.data?.count ?? contacts.length,
        },
        recentPosts: byNewest(posts).slice(0, 6),
        inbox,
        loading: false,
      });
    }).catch(() => {
      this.setState({ loading: false });
    });
  }

  renderInboxItem(item, index) {
    if (item.kind === 'termin') {
      const name = [item.firstname, item.secondname].filter(Boolean).join(' ') || item.name || 'Ohne Namen';
      const car = [item.carMake, item.carModel].filter(Boolean).join(' ');
      return (
        <RouterLink key={item.unikey || item.id || index} className="dash-item" to="/admin/forms">
          <div className="dash-item-top">
            <p className="dash-item-title">
              <span className="dash-tag is-termin">Termin</span>
              {name}
            </p>
            <span className="dash-item-time">{item.created ? moment(item.created).fromNow() : ''}</span>
          </div>
          <p className="dash-item-meta">
            {car ? `${car} · ` : ''}
            {item.emailAddress || item.email || 'Keine E-Mail'}
            {item.dateTime ? ` · Wunsch ${moment(item.dateTime).format('DD.MM.YYYY')}` : ''}
            {item.note ? ` · ${String(item.note).replace(/<[^>]+>/g, '').slice(0, 72)}` : ''}
          </p>
        </RouterLink>
      );
    }

    return (
      <RouterLink key={item.unikey || item.id || index} className="dash-item" to="/admin/forms">
        <div className="dash-item-top">
          <p className="dash-item-title">
            <span className="dash-tag is-kontakt">Kontakt</span>
            {item.name || 'Ohne Namen'}
          </p>
          <span className="dash-item-time">{item.created ? moment(item.created).fromNow() : ''}</span>
        </div>
        <p className="dash-item-meta">
          {item.email || 'Keine E-Mail'}
          {item.message ? ` · ${String(item.message).replace(/<[^>]+>/g, '').slice(0, 72)}` : ''}
        </p>
      </RouterLink>
    );
  }

  render() {
    const { loading, stats, recentPosts, inbox } = this.state;
    const openRequests = (stats.appointments || 0) + (stats.contacts || 0);

    const statCards = [
      { title: 'Beiträge', value: stats.posts, to: '/admin/posts', icon: <PostAddIcon fontSize="small" /> },
      { title: 'Leistungen', value: stats.services, to: '/admin/services', icon: <BuildIcon fontSize="small" /> },
      { title: 'Galerie', value: stats.gallery, to: '/admin/gallery', icon: <PhotoLibraryIcon fontSize="small" /> },
      { title: 'Team', value: stats.employees, to: '/admin/employees', icon: <PeopleIcon fontSize="small" /> },
      { title: 'Termine', value: stats.appointments, to: '/admin/forms', icon: <EventNoteIcon fontSize="small" />, warn: stats.appointments > 0 },
      { title: 'Nachrichten', value: stats.contacts, to: '/admin/forms', icon: <MailOutlineIcon fontSize="small" />, warn: stats.contacts > 0 },
    ];

    return (
      <SecuredZone
        breadcrumbs={{ '/admin/': 'Dashboard' }}
        title="Dashboard"
        loading={loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
      >
        {!loading && (
          <div className="dash">
            <section className="dash-hero">
              <div className="dash-hero-copy">
                <div className="dash-hero-kicker">Administration</div>
                <h2>{greeting()}</h2>
                <p>
                  {openRequests > 0
                    ? `${openRequests} Anfrage${openRequests === 1 ? '' : 'n'} warten auf Sie – Beiträge, Leistungen und Termine im Blick.`
                    : 'Alles ruhig: keine offenen Formular-Anfragen. Inhalte und Termine können Sie hier steuern.'}
                </p>
              </div>
              <div className="dash-hero-meta">
                <span className="dash-hero-date">{moment().format('dddd, D. MMMM YYYY')}</span>
                <a className="dash-hero-site" href="/" target="_blank" rel="noreferrer">
                  Website öffnen
                </a>
              </div>
            </section>

            <div className="dash-stats">
              {statCards.map((card) => (
                <RouterLink
                  key={card.title}
                  to={card.to}
                  className={`dash-stat${card.warn ? ' is-warn' : ''}`}
                >
                  <div className="dash-stat-top">
                    <span className="dash-stat-label">{card.title}</span>
                    <span className="dash-stat-icon">{card.icon}</span>
                  </div>
                  <div className="dash-stat-value">{card.value}</div>
                </RouterLink>
              ))}
            </div>

            <div className="dash-grid">
              <section className="dash-panel">
                <div className="dash-panel-head">
                  <h3>Neueste Anfragen</h3>
                  <RouterLink className="dash-panel-link" to="/admin/forms">Alle Formulare</RouterLink>
                </div>
                <div className="dash-list">
                  {inbox.length > 0 ? inbox.map((item, index) => this.renderInboxItem(item, index)) : (
                    <div className="dash-empty">Noch keine Termine oder Kontaktanfragen.</div>
                  )}
                </div>
              </section>

              <section className="dash-panel">
                <div className="dash-panel-head">
                  <h3>Neueste Beiträge</h3>
                  <RouterLink className="dash-panel-link" to="/admin/posts">Alle Beiträge</RouterLink>
                </div>
                <div className="dash-list">
                  {recentPosts.length > 0 ? recentPosts.map((post, index) => (
                    <RouterLink
                      key={post.unikey || post.id || index}
                      className="dash-item"
                      to="/admin/posts"
                    >
                      <div className="dash-item-top">
                        <p className="dash-item-title">{post.title || 'Ohne Titel'}</p>
                        <span className="dash-item-time">{post.created ? moment(post.created).format('DD.MM.YYYY') : ''}</span>
                      </div>
                      <p className="dash-item-meta">
                        {post.description
                          ? String(post.description).replace(/<[^>]+>/g, '').slice(0, 90)
                          : 'Keine Beschreibung'}
                      </p>
                    </RouterLink>
                  )) : (
                    <div className="dash-empty">Noch keine Beiträge vorhanden.</div>
                  )}
                </div>
              </section>
            </div>

            <div className="dash-actions">
              <RouterLink className="dash-action" to="/admin/posts">
                <PostAddIcon />
                <span>Beitrag schreiben</span>
              </RouterLink>
              <RouterLink className="dash-action" to="/admin/services">
                <BuildIcon />
                <span>Leistung anlegen</span>
              </RouterLink>
              <RouterLink className="dash-action" to="/admin/gallery">
                <PhotoLibraryIcon />
                <span>Galerie pflegen</span>
              </RouterLink>
              <RouterLink className="dash-action" to="/admin/site">
                <WebIcon />
                <span>Website-Texte</span>
              </RouterLink>
            </div>
          </div>
        )}
      </SecuredZone>
    );
  }
}

export default AdminDashboard;
